import { Injectable } from '@angular/core';

import {
    EmailComposer,
    EmailComposerOptions,
} from '@awesome-cordova-plugins/email-composer/ngx';
import { Filesystem } from '@capacitor/filesystem';

import { FileService } from '../file.service';
import { PlatformService } from '../platform.service';
import { TEN_MEGABYTES } from 'src/app/helpers/utils';

/**
 * The only option needed for now is the EmailComposerOptions object,
 * but this will make additional custom properties easy to add later.
 */
export interface OpenEmailClientOptions {
    composerOptions: EmailComposerOptions;
}

@Injectable({
    providedIn: 'root',
})
export class EmailService {
    isWebApp: boolean;

    constructor(
        protected emailComposer: EmailComposer,
        protected platformService: PlatformService,
        protected fileService: FileService
    ) {
        this.isWebApp = this.platformService.isBrowser;
    }

    openEmailClient(options: OpenEmailClientOptions): void {
        if (this.isWebApp) {
            this.openEmailWeb(options);
        } else {
            options.composerOptions.isHtml = true;
            this.openEmailNative(options);
        }
    }

    private openEmailWeb(options: OpenEmailClientOptions): void {
        window.location.href = this.buildMailTo(options);
    }

    private buildMailTo(options: OpenEmailClientOptions) {
        const mailTo = new (class {
            str = 'mailto:?';

            append(param: string, val: string) {
                if (this.str.endsWith('?')) {
                    this.str += `${param}=${val}`;
                } else {
                    this.str += `&${param}=${val}`;
                }
            }
        })();

        if (options.composerOptions.subject) {
            mailTo.append('subject', options.composerOptions.subject);
        }

        if (options.composerOptions.body) {
            mailTo.append('body', options.composerOptions.body);
        }

        return mailTo.str;
    }

    private async openEmailNative(
        options: OpenEmailClientOptions
    ): Promise<void> {
        if (options.composerOptions.attachments) {
            options.composerOptions.attachments =
                await this.onlyAddAttachmentsWithMaximumSizeOf(
                    TEN_MEGABYTES,
                    options.composerOptions.attachments
                );
        }

        this.emailComposer.open(options.composerOptions);
    }

    private async onlyAddAttachmentsWithMaximumSizeOf(
        bytes: number,
        attachments: string[]
    ) {
        if (!attachments || !attachments.length) {
            return attachments;
        }

        const attachmentFileSizeTuple = await Promise.all(
            attachments.map(async (attachment) => {
                // File is stored on device
                if (attachment.startsWith('file://')) {
                    return Filesystem.stat({
                        path: attachment,
                        directory: undefined,
                    }).then((stat) => [stat.size, decodeURIComponent(stat.uri)]);
                }
                // In-memory image - attach it
                else {
                    return Promise.resolve([0, attachment]);
                }
            })
        );

        return attachmentFileSizeTuple
            .map(([size, file]) => (size as number <= bytes ? file : null))
            .filter(Boolean) as string[];
    }
}
